import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import PasswordStrengthIndicator from '@/components/PasswordStrengthIndicator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [passwordValidation, setPasswordValidation] = useState({
    score: 0,
    strength: 'Très faible',
    is_valid: false,
    issues: []
  });
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  // Validation directement dans useEffect pour éviter boucles

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!formData.new_password) {
        setPasswordValidation({
          score: 0,
          strength: 'Très faible',
          is_valid: false,
          issues: []
        });
        return;
      }

      // Validation directe sans dépendance fonction
      const validatePasswordDirect = async () => {
        try {
          const response = await axios.post(`${API}/auth/validate-password`, { password: formData.new_password });
          setPasswordValidation(response.data);
        } catch (error) {
          console.error('Password validation error:', error);
        }
      };

      validatePasswordDirect();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.new_password]);

  // Validate token on page load
  useEffect(() => {
    if (!token) {
      toast.error('Token de réinitialisation manquant');
      navigate('/login');
      return;
    }
    
    // For now, assume token is valid (in production, you'd validate it)
    setTokenValid(true);
    setValidating(false);
  }, [token, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.new_password || !formData.confirm_password) {
      toast.error('Veuillez remplir tous les champs');
      return false;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return false;
    }

    if (!passwordValidation.is_valid) {
      toast.error('Le mot de passe ne respecte pas les critères de sécurité');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: formData.new_password
      });

      toast.success('Mot de passe réinitialisé avec succès !');
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black p-4">
        <Card className="w-full max-w-md shadow-2xl border-gray-200 dark:border-gray-800 dark:border-gray-800">
          <CardContent className="pt-6 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-gray-200 dark:border-gray-800 border-t-slate-600 rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Vérification du token...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black p-4">
        <Card className="w-full max-w-md shadow-2xl border-red-100">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Token invalide</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Ce lien de réinitialisation n'est plus valide ou a expiré.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center w-full bg-slate-800 text-white dark:bg-slate-700 px-4 py-3 rounded-lg font-medium"
            >
              Retour à la connexion
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black p-4">
        <Card className="w-full max-w-md shadow-2xl border-green-100">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Mot de passe réinitialisé !</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Votre mot de passe a été changé avec succès. Vous allez être redirigé vers la page de connexion.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black p-4">
      <Card className="w-full max-w-lg shadow-2xl border-gray-200 dark:border-gray-800 dark:border-gray-800">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
            <KeyRound className="w-9 h-9 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">Firma</CardTitle>
          <CardDescription className="text-base">
            Réinitialiser votre mot de passe
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
            {/* New Password field */}
            <div className="space-y-2">
              <Label htmlFor="new_password">Nouveau mot de passe *</Label>
              <Input
                id="new_password"
                name="new_password"
                type="password"
                placeholder="Votre nouveau mot de passe"
                value={formData.new_password}
                onChange={handleInputChange}
                required
                data-testid="new-password-input"
                className="border-gray-200 dark:border-gray-800 focus:border-slate-500"
              />
              <PasswordStrengthIndicator 
                password={formData.new_password} 
                validation={passwordValidation} 
              />
            </div>

            {/* Confirm Password field */}
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmer le mot de passe *</Label>
              <Input
                id="confirm_password"
                name="confirm_password"
                type="password"
                placeholder="Retapez votre mot de passe"
                value={formData.confirm_password}
                onChange={handleInputChange}
                required
                data-testid="confirm-password-input"
                className={`border-gray-200 dark:border-gray-800 focus:border-slate-500 ${
                  formData.confirm_password && formData.new_password !== formData.confirm_password
                    ? 'border-red-500 bg-red-50'
                    : formData.confirm_password && formData.new_password === formData.confirm_password
                    ? 'border-green-500 bg-green-50'
                    : ''
                }`}
              />
              {formData.confirm_password && formData.new_password !== formData.confirm_password && (
                <p className="text-xs text-red-600">Les mots de passe ne correspondent pas</p>
              )}
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              disabled={loading || !passwordValidation.is_valid}
              data-testid="reset-password-button"
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-medium py-3 text-base disabled:opacity-50"
            >
              {loading ? 'Réinitialisation...' : 'Changer le mot de passe'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-slate-700 dark:text-slate-300 hover:text-slate-800 dark:text-slate-200 font-medium text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Retour à la connexion
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;